import { useCallback, useEffect, useState } from "react";
import { Crown, Loader2, MailPlus, RefreshCw, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/time";
import { Confirm } from "@/components/settings/Confirm";

const input = "h-10 rounded-md bg-[#090D16] border border-slate-700 px-3 text-sm focus:outline-none focus:border-cyan-500 transition-colors";
const INVITE = { sent: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", failed: "text-rose-300 bg-rose-500/10 border-rose-500/30", pending: "text-slate-300 bg-slate-800 border-slate-700" };

const AddForm = ({ onAdded }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team", { email, name });
      data.invite_sent ? toast.success(`Access granted · invite sent to ${data.email}`) : toast.warning(`Access granted, but the invite email failed. Use "Resend invite".`);
      setEmail(""); setName(""); onAdded();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2" data-testid="team-add-form">
      <input data-testid="team-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@company.com" className={`${input} flex-1 min-w-[220px]`} />
      <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className={`${input} w-48`} />
      <button data-testid="team-add-btn" type="submit" disabled={busy || !email}
        className="h-10 px-4 rounded-md bg-cyan-500 text-slate-950 text-sm font-semibold flex items-center gap-2 hover:bg-cyan-400 transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}Add & send invite
      </button>
    </form>
  );
};

const MemberRow = ({ m, onResend, onRemove, busy }) => (
  <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-800 last:border-0 hover:bg-slate-800/30 transition-colors" data-testid={`team-member-${m.email}`}>
    <div className="w-9 h-9 rounded-full bg-slate-800 grid place-items-center font-mono text-sm text-cyan-300 shrink-0">{(m.name || m.email)[0].toUpperCase()}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-100 truncate" data-testid="team-member-email">{m.email}</p>
      <p className="text-xs text-slate-500">{m.name ? `${m.name} · ` : ""}added {timeAgo(m.added_at)} · last sign-in {timeAgo(m.last_login_at)}</p>
    </div>
    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${INVITE[m.invite_status] || INVITE.pending}`} data-testid="team-member-invite-status">
      invite {m.invite_status || "pending"}
    </span>
    <button data-testid="team-resend-btn" onClick={onResend} disabled={busy} title="Resend invite"
      className="h-8 px-2.5 rounded-md border border-slate-700 text-xs text-slate-300 flex items-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <MailPlus size={13} />}Resend invite
    </button>
    <button data-testid="team-remove-btn" onClick={onRemove} title="Remove access"
      className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors"><Trash2 size={15} /></button>
  </div>
);

export const TeamPanel = () => {
  const [team, setTeam] = useState(null);
  const [busy, setBusy] = useState(null);
  const [remove, setRemove] = useState(null);
  const load = useCallback(() => api.get("/team").then(({ data }) => setTeam(data)).catch((x) => toast.error(errMsg(x))), []);
  useEffect(() => { load(); }, [load]);
  const resend = async (email) => {
    setBusy(email);
    try { await api.post(`/team/${encodeURIComponent(email)}/invite`); toast.success(`Invite re-sent to ${email}`); load(); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(null); }
  };
  const doRemove = async () => {
    const email = remove;
    setRemove(null);
    try { await api.delete(`/team/${encodeURIComponent(email)}`); toast.success(`${email} can no longer sign in`); load(); } catch (x) { toast.error(errMsg(x)); }
  };
  if (!team) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>;
  return (
    <div className="space-y-6" data-testid="team-panel">
      <div>
        <h2 className="font-heading text-base md:text-lg font-semibold">Team access</h2>
        <p className="text-sm text-slate-400 mt-1">People you add can sign in with a one-time email code and work in this shared workspace: they can see, create and edit all projects.</p>
      </div>
      <AddForm onAdded={load} />
      <div className="rounded-lg border border-slate-800 bg-[#0d1220]">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800" data-testid="team-owner-row">
          <div className="w-9 h-9 rounded-full bg-amber-500/15 grid place-items-center shrink-0"><Crown size={16} className="text-amber-300" /></div>
          <div className="flex-1 min-w-0"><p className="text-sm text-slate-100 truncate">{team.owner.email}</p><p className="text-xs text-slate-500">Owner · manages team access</p></div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border text-amber-300 bg-amber-500/10 border-amber-500/30">owner</span>
        </div>
        {team.members.map((m) => <MemberRow key={m.email} m={m} busy={busy === m.email} onResend={() => resend(m.email)} onRemove={() => setRemove(m.email)} />)}
        {!team.members.length && <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="team-empty">No staff yet. Add an email above to give someone access.</p>}
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1.5"><RefreshCw size={12} />Removed staff can't request new codes. Devices they're already signed in on keep working until that session ends. To cut access now, sign those devices out under Devices.</p>
      <Confirm open={!!remove} testid="team-remove-dialog" title="Remove team access?" action="Remove access" onCancel={() => setRemove(null)} onConfirm={doRemove}
        body={`${remove} will no longer be able to sign in. Devices they're already signed in on keep working until their current session ends.`} />
    </div>
  );
};
